import { SearchFilterBar } from '../../shared/ui/filters';
import { useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ConfigurationParameterHistoryEntrySchema,
  ConfigurationParameterSummarySchema,
  type ConfigurationParameterSummary,
} from '@taqon/contracts';
import { z } from 'zod';
import { errorMessage, getData, postJson, putJson } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { ConfirmDialog } from '../../shared/ui/confirm';
import { FormDialog } from '../../shared/ui/forms';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { describeValue, ValueEditor } from './ValueEditor';

const PARAMETERS = '/api/v1/platform/configuration-parameters';
const KEY = ['platform', 'configuration-parameters'];

/**
 * Governed configuration (SDD §15.2; FRD-CORE-042, CORE-043, USR-010, USR-011): every parameter
 * the running system declares, its value now in force and any scheduled change. A change is a new
 * audited row — history is never overwritten — and a required parameter that is not set keeps the
 * function that needs it refused (no code defaults). An optional parameter can be returned to "not
 * set" and a scheduled change cancelled before it takes effect; both are recorded rows too.
 */
export function ConfigurationPage() {
  const session = useSession();
  const canChange = hasCapability(session.data, 'platform.configuration_parameter:change');
  const [editing, setEditing] = useState<ConfigurationParameterSummary | null>(null);
  const [history, setHistory] = useState<string | null>(null);
  const [unsetting, setUnsetting] = useState<ConfigurationParameterSummary | null>(null);
  const [cancelling, setCancelling] = useState<ConfigurationParameterSummary | null>(null);
  const [search, setSearch] = useState('');
  const parameters = useQuery({
    queryKey: KEY,
    queryFn: () => getData(PARAMETERS, z.array(ConfigurationParameterSummarySchema)),
  });
  const groups = useMemo(() => {
    const term = search.trim().toLowerCase();
    const byModule = new Map<string, ConfigurationParameterSummary[]>();
    for (const p of parameters.data ?? []) {
      const matches =
        term === '' ||
        p.key.toLowerCase().includes(term) ||
        p.description.toLowerCase().includes(term) ||
        moduleTitle(p.owningModule).toLowerCase().includes(term);
      if (matches) byModule.set(p.owningModule, [...(byModule.get(p.owningModule) ?? []), p]);
    }
    return [...byModule.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [parameters.data, search]);
  const missing = (parameters.data ?? []).filter(
    (p) => p.requiredBeforeUse && p.currentValue === undefined,
  );

  return (
    <Stack spacing={3}>
      <PageHeader
        title="Configuration"
        description="Policy values the system reads at run time. Each change is recorded with who made it and when it takes effect."
      />
      {parameters.isPending && <LoadingState label="Loading configuration" />}
      {parameters.isError && <ErrorState message={parameters.error.message} />}
      {missing.length > 0 && (
        // Parameter keys are long unbroken words: on a phone they wrap rather than run off the box.
        <Alert severity="warning" variant="outlined" sx={{ overflowWrap: 'anywhere' }}>
          {missing.length === 1
            ? '1 required parameter is'
            : `${String(missing.length)} required parameters are`}{' '}
          not set. The functions that depend on {missing.length === 1 ? 'it' : 'them'} are refused
          until {missing.length === 1 ? 'it is' : 'they are'} set:{' '}
          {missing.map((p) => p.key).join(', ')}.
        </Alert>
      )}
      {parameters.data !== undefined && parameters.data.length > 0 && (
        <SearchFilterBar
          label="Search parameters"
          helperText="Matches the key, the description or the area."
          value={search}
          onChange={setSearch}
        />
      )}
      {parameters.data?.length === 0 && (
        <Section>
          <EmptyState title="No parameters are declared" />
        </Section>
      )}
      {parameters.data !== undefined && parameters.data.length > 0 && groups.length === 0 && (
        <Section>
          <EmptyState title="No parameter matches the search" />
        </Section>
      )}
      {groups.map(([module, items]) => (
        <Section key={module} title={moduleTitle(module)}>
          <Table size="small" aria-label={`${moduleTitle(module)} parameters`}>
            <TableHead>
              <TableRow>
                <TableCell>Parameter</TableCell>
                <TableCell>Value in force</TableCell>
                <TableCell>Since</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((p) => (
                <TableRow key={p.key} hover>
                  <TableCell sx={{ minWidth: 240 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {p.key}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {p.description}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 180 }}>
                    {p.currentValue === undefined ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        color={p.requiredBeforeUse ? 'warning' : 'default'}
                        label={p.requiredBeforeUse ? 'Not set — required' : 'Not set'}
                      />
                    ) : (
                      describeValue(p.currentValue)
                    )}
                    {p.scheduledFrom !== null && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block' }}
                      >
                        Scheduled: {p.scheduledUnset ? 'not set' : describeValue(p.scheduledValue)}{' '}
                        from {new Date(p.scheduledFrom).toLocaleString()}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {p.effectiveFrom === null ? '—' : new Date(p.effectiveFrom).toLocaleString()}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Button
                      size="small"
                      onClick={() => {
                        setHistory(p.key);
                      }}
                    >
                      History
                    </Button>
                    {canChange && (
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{ ml: 1 }}
                        onClick={() => {
                          setEditing(p);
                        }}
                      >
                        Change
                      </Button>
                    )}
                    {canChange && !p.requiredBeforeUse && p.currentValue !== undefined && (
                      <Button
                        size="small"
                        sx={{ ml: 1 }}
                        onClick={() => {
                          setUnsetting(p);
                        }}
                      >
                        Return to not set
                      </Button>
                    )}
                    {canChange && p.scheduledFrom !== null && (
                      <Button
                        size="small"
                        color="warning"
                        sx={{ ml: 1 }}
                        onClick={() => {
                          setCancelling(p);
                        }}
                      >
                        Cancel scheduled
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Section>
      ))}
      {editing !== null && (
        <ChangeParameterDialog
          key={editing.key}
          parameter={editing}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}
      {unsetting !== null && (
        <UnsetParameterDialog
          key={unsetting.key}
          parameter={unsetting}
          onClose={() => {
            setUnsetting(null);
          }}
        />
      )}
      {cancelling !== null && (
        <CancelScheduledDialog
          key={cancelling.key}
          parameter={cancelling}
          onClose={() => {
            setCancelling(null);
          }}
        />
      )}
      {history !== null && (
        <HistoryDialog
          parameterKey={history}
          onClose={() => {
            setHistory(null);
          }}
        />
      )}
    </Stack>
  );
}

/** The current minute as a `datetime-local` value: a change cannot be back-dated (SDD §15.2). */
function thisMinute(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${String(now.getFullYear())}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

function moduleTitle(module: string): string {
  const names: Record<string, string> = {
    identity: 'Identity and sessions',
    hr: 'HR',
    vat: 'VAT',
    commercial: 'Commercial',
    crm: 'Clients',
    assets: 'Assets',
    platform: 'Platform',
    procurement: 'Procurement',
    marketing: 'Marketing',
  };
  return names[module] ?? module;
}

function ChangeParameterDialog({
  parameter,
  onClose,
}: {
  parameter: ConfigurationParameterSummary;
  onClose: () => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [value, setValue] = useState<unknown>(parameter.currentValue);
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [reason, setReason] = useState('');
  const save = useMutation({
    mutationFn: () =>
      putJson(
        `${PARAMETERS}/${encodeURIComponent(parameter.key)}`,
        {
          value,
          ...(effectiveFrom !== '' ? { effectiveFrom: new Date(effectiveFrom).toISOString() } : {}),
          ...(reason.trim() !== '' ? { reason: reason.trim() } : {}),
        },
        ConfigurationParameterSummarySchema,
        session.data?.csrfToken ?? '',
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: KEY });
      onClose();
    },
  });
  return (
    <FormDialog
      open
      title={`Change ${parameter.key}`}
      submitLabel="Record value"
      canSubmit={value !== undefined}
      submitting={save.isPending}
      error={save.isError ? save.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        save.mutate();
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {parameter.description}
      </Typography>
      <ValueEditor label="Value" schema={parameter.valueSchema} value={value} onChange={setValue} />
      <TextField
        label="Takes effect"
        type="datetime-local"
        value={effectiveFrom}
        onChange={(e) => {
          setEffectiveFrom(e.target.value);
        }}
        helperText="Leave empty to apply at once. A future time schedules the change; a past time is refused."
        slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: thisMinute() } }}
      />
      <TextField
        label="Reason"
        value={reason}
        onChange={(e) => {
          setReason(e.target.value);
        }}
        helperText="Recorded with the change in the audit trail."
      />
    </FormDialog>
  );
}

/**
 * Returns an optional parameter to "not set": the function reading it falls back to what its
 * source defines when nothing is configured. Recorded and audited like a value.
 */
function UnsetParameterDialog({
  parameter,
  onClose,
}: {
  parameter: ConfigurationParameterSummary;
  onClose: () => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [reason, setReason] = useState('');
  const unset = useMutation({
    mutationFn: () =>
      postJson(
        `${PARAMETERS}/${encodeURIComponent(parameter.key)}/unset`,
        {
          ...(effectiveFrom !== '' ? { effectiveFrom: new Date(effectiveFrom).toISOString() } : {}),
          ...(reason.trim() !== '' ? { reason: reason.trim() } : {}),
        },
        ConfigurationParameterSummarySchema,
        session.data?.csrfToken ?? '',
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: KEY });
      onClose();
    },
  });
  return (
    <ConfirmDialog
      open
      title={`Return ${parameter.key} to not set`}
      message="From the time below the parameter has no value, as if it had never been set. The current value stays in the history."
      confirmLabel="Return to not set"
      tone="warning"
      busy={unset.isPending}
      error={unset.isError ? errorMessage(unset.error) : null}
      onCancel={onClose}
      onConfirm={() => {
        unset.mutate();
      }}
    >
      <Stack spacing={2} sx={{ mt: 2 }}>
        <TextField
          label="Takes effect"
          type="datetime-local"
          value={effectiveFrom}
          onChange={(e) => {
            setEffectiveFrom(e.target.value);
          }}
          helperText="Leave empty to apply at once. A past time is refused."
          slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: thisMinute() } }}
        />
        <TextField
          label="Reason"
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
          }}
          helperText="Recorded in the audit trail."
        />
      </Stack>
    </ConfirmDialog>
  );
}

/** Cancels the scheduled change shown on the page, before it takes effect. */
function CancelScheduledDialog({
  parameter,
  onClose,
}: {
  parameter: ConfigurationParameterSummary;
  onClose: () => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const scheduledFrom = parameter.scheduledFrom ?? '';
  const cancel = useMutation({
    mutationFn: () =>
      postJson(
        `${PARAMETERS}/${encodeURIComponent(parameter.key)}/cancel-scheduled`,
        { scheduledFrom, ...(reason.trim() !== '' ? { reason: reason.trim() } : {}) },
        ConfigurationParameterSummarySchema,
        session.data?.csrfToken ?? '',
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: KEY });
      onClose();
    },
  });
  const target = parameter.scheduledUnset ? 'not set' : describeValue(parameter.scheduledValue);
  return (
    <ConfirmDialog
      open
      title={`Cancel the scheduled change of ${parameter.key}`}
      message={`The change to ${target} from ${new Date(scheduledFrom).toLocaleString()} will not take effect. It stays in the history, marked cancelled.`}
      confirmLabel="Cancel scheduled change"
      tone="warning"
      busy={cancel.isPending}
      error={cancel.isError ? errorMessage(cancel.error) : null}
      onCancel={onClose}
      onConfirm={() => {
        cancel.mutate();
      }}
    >
      <TextField
        label="Reason"
        value={reason}
        onChange={(e) => {
          setReason(e.target.value);
        }}
        helperText="Recorded in the audit trail."
        sx={{ mt: 2 }}
        fullWidth
      />
    </ConfirmDialog>
  );
}

function historyValue(entry: z.infer<typeof ConfigurationParameterHistoryEntrySchema>): string {
  if (entry.kind === 'cancellation') {
    return entry.cancelsEffectiveFrom === null
      ? 'Cancelled a scheduled change'
      : `Cancelled the change scheduled for ${new Date(entry.cancelsEffectiveFrom).toLocaleString()}`;
  }
  const text = entry.kind === 'unset' ? 'Not set' : describeValue(entry.value);
  return entry.cancelled ? `${text} (cancelled before it took effect)` : text;
}

function HistoryDialog({ parameterKey, onClose }: { parameterKey: string; onClose: () => void }) {
  const history = useQuery({
    queryKey: [...KEY, parameterKey, 'history'],
    queryFn: () =>
      getData(
        `${PARAMETERS}/${encodeURIComponent(parameterKey)}/history`,
        z.array(ConfigurationParameterHistoryEntrySchema),
      ),
  });
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>History of {parameterKey}</DialogTitle>
      <DialogContent>
        {history.isPending && <LoadingState label="Loading history" />}
        {history.isError && <ErrorState message={history.error.message} />}
        {history.data?.length === 0 && <EmptyState title="Never set" />}
        {history.data !== undefined && history.data.length > 0 && (
          <Table size="small" aria-label="Parameter history">
            <TableHead>
              <TableRow>
                <TableCell>Value</TableCell>
                <TableCell>Effective from / made</TableCell>
                <TableCell>Set by</TableCell>
                <TableCell>Recorded</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.data.map((entry, index) => (
                <TableRow key={index}>
                  <TableCell>{historyValue(entry)}</TableCell>
                  <TableCell>{new Date(entry.effectiveFrom).toLocaleString()}</TableCell>
                  <TableCell>{entry.setBy ?? '—'}</TableCell>
                  <TableCell>{new Date(entry.setAt).toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
